import React, { useEffect, useState } from 'react'
import useGenericFetchHook, { useLazyGenericFetchHook } from '../../../../../inplant-components-fe/mvfunctions/hooks/useGenericFetchHook'
import { useComponentsTranslation } from '../../../../../inplant-components-fe/services/translation'
import { getResetPassword } from '../../../../redux/actions/resetPassword'
import { getUserRoles, UserRoleWithId } from '../../../../redux/actions/userRoles'
import { getPlants } from '../../../../redux/actions/getPlant'
import { getPasswordControlByConfiguration } from '../../../../functions/controlsHelpers'
import { CenterLoader, Checkbox, Flex, StretchColumn } from '../../../../../mvlabs-components-fe/ui/components'
import Button from '../../../../../inplant-components-fe/ui/components/MVButtons/Button'
import Input, { MultiSelect } from '../../../../../inplant-components-fe/ui/components/MVInput/Input'
import { useStateValidity } from '../../../../../inplant-components-fe/mvfunctions/hooks/useGenericInputHook'
import { BreadcrumbsTitle } from '../../../../../inplant-core-fe'
import { Direction } from '../../../../../mvlabs-components-fe/ui/components/Flex/Flex'
import { AlignSelf } from '../../../../../mvlabs-components-fe/ui/components/Flex/FlexItem'
import VidimWrapper from '../../../../../inplant-core-fe/ui/components/widgets/VidimWrapper/VidimWrapperHOC'
import { createUser } from '../../../../redux/actions/createUser'
import { manageRequestErrors } from '../../../../../inplant-core-fe/functions'
import { showNotification } from '../../../../../mvlabs-components-fe/ui/components/Notification/Notification'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { PasswordOptionType, PlantOptionType } from '../../../../types/options'

export interface UserFormData {
  name?: string
  surname?: string
  email?: string
  roles: string[]
  plants: string[]
  enabled: boolean
  password: string
  confirmPassword: string
  username: string
  displayName: string
}

const CreateUserPageView = () => {
  const { t } = useComponentsTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const base = 'user.createUser'
  const baseF = 'user.createUser.form'

  const [isLoading, setIsLoading] = useState(false)

  const [, addDataValidity, isValid] = useStateValidity({})
  const [formData, setFormData] = useState<UserFormData>({
    confirmPassword: '',
    password: '',
    username: '',
    email: undefined,
    name: undefined,
    plants: [],
    roles: [],
    surname: undefined,
    displayName: '',
    enabled: false,
  })

  const [errorToShow, setErrorToShow] = useState<{ error: string | undefined }>({ error: undefined })
  useEffect(() => {
    if (formData.password !== formData.confirmPassword) {
      setErrorToShow({
        error: t(`user.noMatchConfirmPassword`),
      })
    } else {
      setErrorToShow({
        error: undefined,
      })
    }
  }, [formData.password, formData.confirmPassword])

  const [createUserHook,] = useLazyGenericFetchHook(createUser, 
    data => {
      setIsLoading(false)
      showNotification({
        type: 'success',
        title: t(`${baseF}.createSuccess`)
      })
      history.push('/user/list')
    }, 
    error => {
      setIsLoading(false)
      manageRequestErrors(error, dispatch)
  })

  const passwordOptionHook = useGenericFetchHook<PasswordOptionType>(getResetPassword)

  const userRolesHook = useGenericFetchHook<UserRoleWithId[]>(getUserRoles)

  const plantsHook = useGenericFetchHook<PlantOptionType[]>(getPlants)

  function updatePassword(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setIsLoading(true)
    createUserHook(formData)
  }

  function renderSuccessContent(passwordOption: PasswordOptionType, userRoles: UserRoleWithId[], plant: PlantOptionType[]) {
    return (
      <div className="inplant-user inplant-user-form">
        <StretchColumn>
          <BreadcrumbsTitle level={1} title={t(`${base}.title`)} />
          <form onSubmit={updatePassword}>
            <StretchColumn>
              {/* <Alert text={t(`${base}.info`)} variant={'info'} /> */}
              <Flex spaceSize={'lg'} >
                <Input
                  label={t(`${baseF}.name`)}
                  type={'text'}
                  kind={'input'}
                  onChange={value => setFormData(prev => ({ ...prev, name: value }))}
                  onChangeState={state => addDataValidity('name', state)}
                  grow={1}
                />
                <Input
                  label={t(`${baseF}.surname`)}
                  type={'text'}
                  kind={'input'}
                  onChange={value => setFormData(prev => ({ ...prev, surname: value }))}
                  onChangeState={state => addDataValidity('surname', state)}
                  grow={1}
                />
              </Flex>
              <Flex spaceSize={'lg'} >
                <Input
                  label={t(`${baseF}.displayName`)}
                  type={'text'}
                  kind={'input'}
                  onChange={value => setFormData(prev => ({ ...prev, displayName: value }))}
                  required={true}
                  onChangeState={state => addDataValidity('displayName', state)}
                  grow={1}
                />
                <Input
                  label={t(`${baseF}.email`)}
                  type={'text'}
                  kind={'input'}
                  required={true}
                  onChange={value => setFormData(prev => ({ ...prev, email: value }))}
                  onChangeState={state => addDataValidity('email', state)}
                  grow={1}
                />
              </Flex>
              <Flex spaceSize={'lg'} >
                <Input
                  label={t(`${baseF}.username`)}
                  type={'text'}
                  kind={'input'}
                  autocomplete={'off'}
                  onChange={value => setFormData(prev => ({ ...prev, username: value }))}
                  required={true}
                  onChangeState={state => addDataValidity('username', state)}
                  grow={1}
                />
                <Input
                  grow={1}
                  kind={'input'}
                  type={'password'}
                  label={t(`${baseF}.password`)}
                  autocomplete={'off'}
                  id={'field1'}
                  initialValue={formData.password}
                  required={true}
                  controls={getPasswordControlByConfiguration(passwordOption.controls, t)}
                  onChangeState={state => addDataValidity('newPassord', state)}
                  onChange={value => setFormData(prev => ({ ...prev, password: value }))}
                />
              </Flex>
              <Flex spaceSize={'lg'} >
                <Input
                  shrink={0}
                  grow={0}
                  basis={50}
                  kind={'input'}
                  type={'password'}
                  id={'field2'}
                  label={t(`${baseF}.passwordConfirm`)}
                  autocomplete={'off'}
                  initialValue={formData.confirmPassword}
                  required={true}
                  onChangeState={state => addDataValidity('newPassworConfirm', state)}
                  onChange={value => setFormData(prev => ({ ...prev, confirmPassword: value }))}
                  error={errorToShow.error}
                />
                <Checkbox
                  items={[
                    {
                      value: 'enable',
                      label: t(`${baseF}.enable`),
                    },
                  ]}
                  itemsDirection={Direction.row}
                  onChange={value => setFormData(prev => ({...prev, enabled: value[0].selected}))}
                />
              </Flex>
              <Flex spaceSize={'lg'} >
                <MultiSelect 
                  options={{
                    items: userRoles.map(r => ({label: r.name, value: r.id}))
                  }}
                  required={true}
                  grow={1}
                  label={t(`${baseF}.roles`)}
                  onChange={value => setFormData(prev => ({...prev, roles: value.filter(v => v.selected).map(v => v.value)}))}
                  onChangeState={state => addDataValidity('newPassworConfirm', state)}
                />
                <MultiSelect
                  options={{
                    items: plant.map(r => ({label: r.name, value: r.id}))
                  }}
                  required={true}
                  grow={1}
                  label={t(`${baseF}.plants`)}
                  onChange={value => {
                    setFormData(prev => ({...prev, plants: value.filter(v => v.selected).map(v => v.value)}))}
                  }
                  onChangeState={state => addDataValidity('plants', state)}
                />
              </Flex>
              <Button
                isDisable={isLoading || !isValid || errorToShow.error !== undefined}
                isLoading={isLoading}
                type={'submit'}
                semantic={'primary'}
                alignSelf={AlignSelf.end}
                shrink={1}
                label={t(`${baseF}.save`)}
              />
            </StretchColumn>
          </form>
        </StretchColumn>
      </div>
    )
  }

  function renderContent() {
    if (
      passwordOptionHook.state.kind === 'success' &&
      userRolesHook.state.kind === 'success' &&
      plantsHook.state.kind === 'success'
    ) {
      return renderSuccessContent(passwordOptionHook.state.data, userRolesHook.state.data, plantsHook.state.data)
    }
    if (
      passwordOptionHook.state.kind === 'error' &&
      userRolesHook.state.kind === 'error' &&
      plantsHook.state.kind === 'error'
    ) {
      return null
    }
    return <CenterLoader />
  }

  return renderContent()
}

export default VidimWrapper(CreateUserPageView) 
